import React from "react";
import {
    Stack,
    Divider,
    Accordion,
    AccordionGroup,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from "@mui/joy";
import RelatedFragmentNode from "./RelatedFragmentNode";
import FloatingRelatedItemsLoadingIndicator from "./FloatingRelatedItemsLoadingIndicator";
import { ApiFragmentNode } from "../../shared/interfaces";
import ExpandMore from "@mui/icons-material/ExpandMore";

interface RelatedSpecItemsProps {
    specNodes: ApiFragmentNode[];
    setFragmentNode: (node: ApiFragmentNode | null) => void;
    isLoading: boolean;
}

// Helper to extract CSI code and section title from the spec title
function extractCsiSection(title: string): { code: string; section: string } {
    // Example: "07 00 00 · Thermal and Moisture Protection - REFERENCE STANDARDS: 1.3"
    // Should extract: code = "07 00 00", section = "Thermal and Moisture Protection"
    const match = title.match(/^(\d{2}\s\d{2}\s\d{2})\s+(.+?)\s+-\s+/);
    if (match) {
        return { code: match[1], section: match[2].trim() };
    }
    // fallback: try to get up to first ' - '
    const dashIdx = title.indexOf(" - ");
    if (dashIdx > 0) {
        const beforeDash = title.slice(0, dashIdx).trim();
        const codeMatch = beforeDash.match(/^(\d{2} \d{2} \d{2})/);
        return {
            code: codeMatch ? codeMatch[1] : beforeDash,
            section: beforeDash.replace(/^(\d{2} \d{2} \d{2})\s*/, ""),
        };
    }
    return { code: "", section: title };
}

export default function RelatedSpecItems({
    specNodes,
    setFragmentNode,
    isLoading,
}: RelatedSpecItemsProps) {
    const grouped = React.useMemo(() => {
        const map: Record<string, { code: string; section: string; nodes: ApiFragmentNode[] }> = {};
        for (const node of specNodes) {
            if (!node.title) continue;
            const { code, section } = extractCsiSection(node.title);
            const groupKey = code + " " + section;
            if (!map[groupKey]) {
                map[groupKey] = { code, section, nodes: [] };
            }
            map[groupKey].nodes.push(node);
        }
        // Sort by code
        return Object.values(map).sort((a, b) => a.code.localeCompare(b.code));
    }, [specNodes]);

    return (
        <AccordionGroup>
            {grouped.map((group) => (
                <Accordion key={group.code + group.section}>
                    <AccordionSummary indicator={<ExpandMore />}>
                        <Typography level="title-sm">
                            {group.code} {group.section} ({group.nodes.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2} divider={<Divider />}>
                            {group.nodes.map((node) => (
                                <RelatedFragmentNode
                                    key={node.id}
                                    node={node}
                                    setFragmentNode={setFragmentNode}
                                />
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            ))}
            {isLoading && <FloatingRelatedItemsLoadingIndicator />}
        </AccordionGroup>
    );
}
